import { Outlet, matchPath, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { Suspense, useState } from 'react'
import { useNotificationStream } from '@/hooks/useNotificationStream'
import { LazyFallback } from '@/components/ui/LazyFallback'
import { AssistantProvider } from '@/components/assistant/AssistantContext'
import { AssistantPanel } from '@/components/assistant/AssistantPanel'
import { canUseAssistant } from '@/components/assistant/assistant-roles'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Stránky s layoutom na plnú výšku (koreň `h-full` v <main>, vlastné vnútorné scrollovanie).
 * Rezerva pre FAB asistenta by im ubrala výšku a pod obsahom by ostal prázdny pás —
 * FAB tu leží nad vlastným scroll kontajnerom stránky, nie nad koncom dokumentu.
 */
const FULL_HEIGHT_ROUTES = ['/crm/pipeline', '/emails', '/emails/:folder']

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user } = useAuth()
  const { pathname } = useLocation()
  useNotificationStream()
  const isFullHeight = FULL_HEIGHT_ROUTES.some((path) => matchPath(path, pathname))
  // Rezerva pod obsahom, aby FAB asistenta (fixed vpravo dole, 56 px) nezakryl tlačidlá na konci stránky.
  const reserveFabSpace = canUseAssistant(user) && !isFullHeight

  return (
    <AssistantProvider>
      <div className="flex min-h-dvh lg:h-screen lg:overflow-hidden bg-gray-50 dark:bg-gray-900">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </aside>

        {/* Main content */}
        {/* overflow-x-clip (nie hidden): pretečený obsah na mobile nerozšíri dokument (prehliadač by stránku
            oddialil a horný panel by ostal na šírku viewportu), a clip nevytvára scroll kontajner — sticky panel funguje */}
        <div className="flex flex-1 flex-col min-w-0 overflow-x-clip lg:overflow-hidden">
          <TopBar onMenuClick={() => setSidebarOpen(true)} />
          {/* Menší horný odstup než bočné/spodný — hlavička stránky má byť bližšie k TopBaru
              (sticky panel v PropertyFormPage s ním počíta vo výške). */}
          <main className={`flex-1 px-4 pt-3 sm:px-6 sm:pt-4 lg:overflow-y-auto ${reserveFabSpace ? 'pb-24' : 'pb-4 sm:pb-6'}`}>
            <Suspense fallback={<LazyFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
        <AssistantPanel />
      </div>
    </AssistantProvider>
  )
}
